import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FORALogomark } from '../components/ui/fora-logomark';
import { useSettings } from '../contexts/SettingsContext';
import { Activity, Droplets, Heart, Apple, Wind, Brain, Sun, ShieldPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;
const TIP_ROTATE_MS = 8000;

const formatTime = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

const HEALTH_TIPS = [
  {
    icon: Droplets, accent: '#3D5A80',
    title: 'Stay hydrated',
    body: 'Drink at least 8 glasses of water a day. Even mild dehydration can affect focus, mood and energy.',
    image: 'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&w=1600&q=80',
  },
  {
    icon: Heart, accent: '#B5612E',
    title: 'Move every hour',
    body: 'Sitting for long periods is hard on your back and heart. Stand up, stretch, walk for 2 minutes every hour.',
    image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=1600&q=80',
  },
  {
    icon: Apple, accent: '#16A34A',
    title: 'Half your plate',
    body: 'Aim for half your plate to be vegetables and fruit at every main meal. Color = nutrients.',
    image: 'https://images.unsplash.com/photo-1610348725531-843dff563e2c?auto=format&fit=crop&w=1600&q=80',
  },
  {
    icon: Wind, accent: '#3D5A80',
    title: 'Breathe deeply',
    body: '4-7-8 breathing: inhale 4s, hold 7s, exhale 8s. A simple way to calm your nervous system before a consultation.',
    image: 'https://images.unsplash.com/photo-1545389336-cf090694435e?auto=format&fit=crop&w=1600&q=80',
  },
  {
    icon: Brain, accent: '#7C3AED',
    title: 'Sleep is medicine',
    body: 'Adults need 7–9 hours of sleep. Consistent bedtime is more important than weekend catch-up.',
    image: 'https://images.unsplash.com/photo-1631157769208-4cb3a16fef10?auto=format&fit=crop&w=1600&q=80',
  },
  {
    icon: Sun, accent: '#F59E0B',
    title: 'Get some sun',
    body: 'Just 10–15 minutes of morning sunlight helps regulate sleep, mood and Vitamin D levels.',
    image: 'https://images.unsplash.com/photo-1506953823976-52e1fdc0149a?auto=format&fit=crop&w=1600&q=80',
  },
  {
    icon: ShieldPlus, accent: '#16A34A',
    title: 'Hand hygiene',
    body: 'Wash your hands for 20 seconds with soap. Cover coughs and sneezes — protect those around you.',
    image: 'https://images.unsplash.com/photo-1584634731339-252c581abfc5?auto=format&fit=crop&w=1600&q=80',
  },
  {
    icon: Activity, accent: '#B91C1C',
    title: 'Know your numbers',
    body: 'Blood pressure, blood sugar and cholesterol — get them checked at least once a year, even if you feel fine.',
    image: 'https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?auto=format&fit=crop&w=1600&q=80',
  },
];

/**
 * Public, unauthenticated waiting-room TV board.
 * Left half: live queue management.
 * Right half: rotating health advisory.
 */
export const DisplayView = () => {
  const { settings } = useSettings();
  const [data, setData] = useState({ patients: [], last_updated: null });
  const [now, setNow] = useState(new Date());
  const [error, setError] = useState(null);
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    let stopped = false;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${API}/queue/public`);
        if (!stopped) {
          setData(res.data);
          setError(null);
        }
      } catch (e) {
        if (!stopped) setError(e.message);
      }
    };
    fetchOnce();
    const i = setInterval(fetchOnce, POLL_MS);
    return () => { stopped = true; clearInterval(i); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTipIdx(i => (i + 1) % HEALTH_TIPS.length), TIP_ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  const inConsultation = data.patients.find(p => p.status === 'in-consultation');
  const nextInLineList = data.patients.filter(p => p.status === 'next-in-line');
  const waiting        = data.patients.filter(p => p.status === 'waiting');
  const inQueue        = data.patients.filter(p => p.status === 'in-queue');
  const totalAhead     = waiting.length + inQueue.length + nextInLineList.length;

  const tip = HEALTH_TIPS[tipIdx];
  const TipIcon = tip.icon;

  return (
    <div
      className="min-h-screen w-full overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at 15% 15%, rgba(232,155,111,0.10) 0%, transparent 45%),' +
          'radial-gradient(ellipse at 85% 85%, rgba(61,90,128,0.10) 0%, transparent 50%),' +
          'linear-gradient(180deg, #FDFCF9 0%, #F8F4EC 100%)',
        fontFamily: 'Inter, sans-serif'
      }}
      data-testid="display-view"
    >
      {/* Subtle background art */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><g fill='none' stroke='rgba(61,90,128,0.05)' stroke-width='1'><path d='M40 32v16M32 40h16'/><path d='M120 112v16M112 120h16'/></g><circle cx='80' cy='80' r='1.2' fill='rgba(212,120,74,0.07)'/></svg>\")",
          backgroundSize: '160px 160px'
        }}
      />

      <div className="relative px-10 py-8 h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-copper-gradient flex items-center justify-center" style={{ boxShadow: '0 14px 28px rgba(181, 97, 46, 0.30)' }}>
              <FORALogomark size="md" />
            </div>
            <div>
              <div className="font-['Sora'] text-2xl font-bold tracking-tight" style={{ color: '#1C1917' }}>{settings.clinic_name}</div>
              <div className="font-['Inter'] text-xs mt-0.5" style={{ color: '#78716C' }}>Patient Waiting Display · Powered by FORA</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-['DM_Mono'] text-4xl font-medium tabular-nums" style={{ color: '#1C1917' }}>{formatTime(now)}</div>
            <div className="font-['Inter'] text-xs mt-1" style={{ color: '#78716C' }}>
              {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
        </div>

        {/* Body: split 50/50 */}
        <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
          {/* LEFT — Queue management */}
          <div className="flex flex-col gap-4 min-h-0">
            {/* Now Serving */}
            <div
              className="glass-liquid rounded-[28px] p-7 relative"
              style={{
                background: 'linear-gradient(135deg, rgba(61,90,128,0.92) 0%, rgba(44,67,97,0.92) 100%)',
                color: '#FFFFFF',
              }}
              data-testid="now-serving"
            >
              <div className="font-['Sora'] text-[11px] font-bold tracking-[0.30em] mb-2" style={{ color: 'rgba(255,255,255,0.70)' }}>NOW SERVING</div>
              {inConsultation ? (
                <>
                  <div className="font-['Sora'] text-[90px] font-bold leading-none mb-2" style={{ letterSpacing: '-0.04em' }} data-testid="now-serving-token">
                    {inConsultation.token_number}
                  </div>
                  <div className="font-['Sora'] text-2xl font-medium" style={{ color: 'rgba(255,255,255,0.88)' }}>
                    {inConsultation.patient_display}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(212, 120, 74, 0.30)', border: '1px solid rgba(212, 120, 74, 0.55)' }}>
                    <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#FCD9C0' }} />
                    <span className="font-['Sora'] text-xs font-semibold">IN CONSULTATION</span>
                  </div>
                </>
              ) : (
                <div className="font-['Sora'] text-3xl font-medium py-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Doctor will resume shortly
                </div>
              )}
            </div>

            {/* Next In Line — supports multiple */}
            <div
              className="rounded-[28px] p-6"
              style={{
                background: 'linear-gradient(135deg, rgba(255,232,210,0.85) 0%, rgba(255,218,192,0.85) 100%)',
                border: '1px solid rgba(212, 120, 74, 0.20)',
                boxShadow: 'var(--shadow-glass)',
              }}
              data-testid="next-in-line"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-['Sora'] text-[11px] font-bold tracking-[0.30em]" style={{ color: '#B5612E' }}>
                  NEXT IN LINE {nextInLineList.length > 0 && `· ${nextInLineList.length}`}
                </div>
                <div className="font-['Sora'] text-[10px] font-semibold" style={{ color: '#B5612E', opacity: 0.7 }}>
                  Please be ready
                </div>
              </div>

              {nextInLineList.length === 0 && (
                <div className="font-['Sora'] text-2xl font-medium py-2" style={{ color: '#78716C' }}>—</div>
              )}

              {nextInLineList.length > 0 && (
                <div className="flex flex-col gap-2.5">
                  {nextInLineList.map((p, idx) => (
                    <div
                      key={p.token_number}
                      className="flex items-baseline justify-between px-4 py-2 rounded-[16px]"
                      style={{
                        background: idx === 0 ? 'rgba(255, 255, 255, 0.65)' : 'rgba(255, 255, 255, 0.40)',
                        border: '1px solid rgba(255, 255, 255, 0.80)',
                      }}
                      data-testid={`next-in-line-card-${p.token_number}`}
                    >
                      <div className="flex items-baseline gap-3">
                        <span
                          className="font-['Sora'] font-bold"
                          style={{
                            color: '#B5612E',
                            fontSize: idx === 0 ? '14px' : '12px',
                            opacity: idx === 0 ? 1 : 0.7,
                            minWidth: '24px',
                          }}
                        >
                          {idx + 1}
                        </span>
                        <span
                          className="font-['Sora'] font-bold"
                          style={{
                            color: '#1C1917',
                            fontSize: idx === 0 ? '46px' : '32px',
                            letterSpacing: '-0.03em',
                            lineHeight: 1,
                          }}
                        >
                          {p.token_number}
                        </span>
                      </div>
                      <div
                        className="font-['Sora'] font-medium text-right"
                        style={{
                          color: '#44403C',
                          fontSize: idx === 0 ? '18px' : '14px',
                          opacity: idx === 0 ? 1 : 0.8,
                        }}
                      >
                        {p.patient_display}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Counts row */}
            <div className="grid grid-cols-3 gap-3">
              <CountTile label="Waiting"  value={waiting.length} accent="slate" />
              <CountTile label="In Queue" value={inQueue.length} accent="muted" />
              <CountTile label="Ahead"    value={totalAhead}     accent="copper" />
            </div>

            {/* Waiting list (full, scrollable if many) */}
            <div className="glass-liquid rounded-[24px] p-5 flex-1 min-h-0 flex flex-col" data-testid="waiting-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-['Sora'] text-base font-bold" style={{ color: '#1C1917' }}>Waiting</h3>
                <span className="font-['Sora'] text-xs font-semibold" style={{ color: '#78716C' }}>{waiting.length} patients</span>
              </div>
              <div className="overflow-y-auto flex-1 -mr-2 pr-2">
                {waiting.length === 0 && (
                  <div className="font-['Sora'] text-sm py-6 text-center" style={{ color: '#A8A29E' }}>No one waiting</div>
                )}
                <div className="flex flex-col gap-2">
                  {waiting.map((p, idx) => (
                    <div
                      key={p.token_number}
                      className="flex items-center justify-between px-4 py-2.5 rounded-[14px]"
                      style={{
                        background: '#FFFFFF',
                        border: p.is_emergency ? '2px solid rgba(185,28,28,0.35)' : '1px solid rgba(28, 25, 23, 0.05)',
                        boxShadow: '0 4px 10px rgba(28, 25, 23, 0.04)'
                      }}
                      data-testid={`waiting-card-${p.token_number}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-['Sora'] text-base font-bold w-6 text-center" style={{ color: '#A8A29E' }}>#{idx + 1}</span>
                        <span className="font-['DM_Mono'] text-xl font-semibold" style={{ color: p.is_emergency ? '#B91C1C' : 'var(--color-slate)' }}>
                          {p.token_number}
                        </span>
                      </div>
                      <span className="font-['Inter'] text-sm font-medium" style={{ color: '#44403C' }}>{p.patient_display}</span>
                    </div>
                  ))}
                </div>
              </div>
              {inQueue.length > 0 && (
                <div className="mt-3 pt-3 border-t border-dashed" style={{ borderColor: 'rgba(28,25,23,0.08)' }}>
                  <div className="text-overline mb-1.5" style={{ color: '#78716C' }}>NOT YET ARRIVED · {inQueue.length}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {inQueue.map(p => (
                      <span
                        key={p.token_number}
                        className="font-['DM_Mono'] text-xs px-2 py-1 rounded-full"
                        style={{ background: 'rgba(120, 113, 108, 0.10)', color: '#78716C' }}
                      >
                        {p.token_number}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Health advisory */}
          <div className="flex flex-col gap-4 min-h-0">
            {/* Image-led wellness tip — cinematic */}
            <div
              className="relative rounded-[28px] overflow-hidden flex-1 min-h-0"
              data-testid="health-tip-card"
              style={{
                boxShadow:
                  '0 30px 60px -18px rgba(28, 25, 23, 0.30), 0 12px 24px -8px rgba(28, 25, 23, 0.15)',
              }}
            >
              {/* Stack of images so the previous tip cross-fades to the new one */}
              {HEALTH_TIPS.map((t, i) => (
                <div
                  key={i}
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${t.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: i === tipIdx ? 1 : 0,
                    transform: i === tipIdx ? 'scale(1.0)' : 'scale(1.06)',
                    transition: 'opacity 900ms ease, transform 8000ms ease-out',
                    willChange: 'opacity, transform',
                  }}
                />
              ))}

              {/* Color wash so each tip's accent tints the image */}
              <div
                aria-hidden
                style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(135deg, ${tip.accent}28 0%, transparent 45%), linear-gradient(180deg, transparent 25%, rgba(15, 15, 15, 0.55) 70%, rgba(15, 15, 15, 0.85) 100%)`,
                  transition: 'background 600ms ease',
                }}
              />

              {/* Top-right pagination dots */}
              <div className="absolute top-5 right-5 flex items-center gap-1.5 z-10">
                {HEALTH_TIPS.map((_, i) => (
                  <span
                    key={i}
                    style={{
                      display: 'inline-block',
                      width: i === tipIdx ? '22px' : '6px',
                      height: '6px',
                      borderRadius: '9999px',
                      background: i === tipIdx ? '#FFFFFF' : 'rgba(255,255,255,0.40)',
                      transition: 'all 250ms ease',
                      boxShadow: i === tipIdx ? '0 2px 6px rgba(0,0,0,0.25)' : 'none',
                    }}
                  />
                ))}
              </div>

              {/* Top-left badge */}
              <div className="absolute top-5 left-5 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                   style={{
                     background: 'rgba(255,255,255,0.18)',
                     backdropFilter: 'blur(20px) saturate(160%)',
                     WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                     border: '1px solid rgba(255,255,255,0.35)',
                   }}>
                <TipIcon size={14} strokeWidth={2} style={{ color: '#FFFFFF' }} />
                <span className="font-['Sora'] text-[10px] font-bold tracking-[0.30em]" style={{ color: '#FFFFFF' }}>
                  WELLNESS TIP
                </span>
              </div>

              {/* Bottom glass plate with title + body */}
              <div
                className="absolute left-5 right-5 bottom-5 rounded-[22px] p-6 z-10"
                key={`tip-${tipIdx}`}
                style={{
                  background: 'rgba(255, 255, 255, 0.10)',
                  backdropFilter: 'blur(28px) saturate(170%) brightness(112%)',
                  WebkitBackdropFilter: 'blur(28px) saturate(170%) brightness(112%)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  boxShadow: '0 14px 40px rgba(0, 0, 0, 0.30), inset 0 1px 0 0 rgba(255,255,255,0.35)',
                  animation: 'slideUp 0.7s ease-out',
                }}
              >
                <h2
                  className="font-['Sora'] text-[44px] font-bold leading-tight mb-2"
                  style={{
                    color: '#FFFFFF',
                    letterSpacing: '-0.02em',
                    textShadow: '0 2px 18px rgba(0,0,0,0.40)',
                  }}
                  data-testid="health-tip-title"
                >
                  {tip.title}
                </h2>
                <p
                  className="font-['Inter'] text-base leading-relaxed"
                  style={{
                    color: 'rgba(255,255,255,0.92)',
                    maxWidth: '46ch',
                    textShadow: '0 1px 8px rgba(0,0,0,0.30)',
                  }}
                  data-testid="health-tip-body"
                >
                  {tip.body}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Activity size={12} strokeWidth={2} style={{ color: 'rgba(255,255,255,0.65)' }} />
                  <span className="font-['Inter'] text-[10px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
                    Rotates every 8 seconds · Not a substitute for medical advice
                  </span>
                </div>
              </div>
            </div>

            {/* Clinic info strip */}
            <div className="grid grid-cols-2 gap-3">
              <div className="glass-subtle rounded-[18px] p-4">
                <div className="text-overline" style={{ color: '#78716C' }}>EMERGENCY</div>
                <div className="font-['Sora'] text-lg font-bold mt-1" style={{ color: '#B91C1C' }}>108</div>
              </div>
              <div className="glass-subtle rounded-[18px] p-4">
                <div className="text-overline" style={{ color: '#78716C' }}>WI-FI</div>
                <div className="font-['Sora'] text-sm font-semibold mt-1" style={{ color: '#1C1917' }}>FORA-Guest · open</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between font-['Inter'] text-xs" style={{ color: '#A8A29E' }}>
          <span>Auto-refreshes every 5 seconds · No sign-in required</span>
          <span>{error ? '⚠️ Offline' : `✓ Live · ${formatTime(now)}`}</span>
        </div>
      </div>
    </div>
  );
};

const CountTile = ({ label, value, accent = 'slate' }) => {
  const accents = {
    slate:  { color: 'var(--color-slate)', bg: 'rgba(61,90,128,0.10)' },
    copper: { color: '#B5612E',           bg: 'rgba(212,120,74,0.10)' },
    muted:  { color: '#78716C',           bg: 'rgba(120,113,108,0.10)' },
  };
  const a = accents[accent] || accents.slate;
  return (
    <div className="glass-liquid rounded-[18px] p-4" data-testid={`count-${label.toLowerCase()}`}>
      <div className="text-overline" style={{ color: '#78716C' }}>{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="font-['DM_Mono'] text-3xl font-semibold" style={{ color: a.color }}>{value}</span>
      </div>
    </div>
  );
};

export default DisplayView;
